import * as gql from "gql-query-builder";

import {
  AccountsBalancesPayload,
  UserPayload,
  EstimatePaymentFeePayload,
  PaymentsLimitsPayload,
  CalculatePaymentsLimitsPayload,
  CreateWithdrawalFiatPayload,
  PaymentsRoutesPayload,
} from "./types";

import {
  accountsBalancesFields,
  userFields,
  paymentsLimitsFields,
  calculatedPaymentsLimitsFields,
  createWithdrawalFiatFields,
  paymentsRoutesFields,
} from "./fields";

export enum Operations {
  GET_ACCOUNTS_BALANCES = "accounts_balances",
  GET_USER = "user",
  CALCULATE_PAYMENTS_LIMITS = "calculate_payments_limits",
  ESTIMATE_PAYMENT_FEE = "estimate_payment_fee",
  GET_PAYMENTS_LIMITS = "payments_limits",
  CREATE_WITHDRAWAL_FIAT = "create_withdrawal_fiat",
  GET_PAYMENTS_ROUTES = "payments_routes",
}

/**
 * TODO: remove after andx upgrades to vakotrade 4.35.
 * TEMPORARY: andx prod/uat backends (~4.31) predate multi-wallet — no
 * `wallet_id` on `AccountBalance` nor as a query/mutation arg. Sending it
 * fails GraphQL validation and blanks the widget.
 */
const isLegacyAndxExchange = (): boolean => {
  const exchange = (new URLSearchParams(window.location.search).get("exchange") ?? "")
    .trim()
    .toUpperCase();
  return exchange === "ANDX" || exchange === "ANDX-UAT";
};

export const v4Operations = {
  getAccountsBalances: (variables: AccountsBalancesPayload) => {
    const omitWalletId = isLegacyAndxExchange();
    return gql.query({
      operation: Operations.GET_ACCOUNTS_BALANCES,
      variables: {
        user_id: { value: variables.user_id, required: false, type: "String" },
        currency_id: { value: variables.currency_id, required: false, type: "String" },
        hide_zero_balances: {
          value: variables.hide_zero_balances,
          required: false,
          type: "ToggleSwitch",
        },
        ...(omitWalletId
          ? {}
          : { wallet_id: { value: variables.wallet_id, required: false, type: "String" } }),
      },
      fields: omitWalletId
        ? accountsBalancesFields.filter((field) => field !== "wallet_id")
        : accountsBalancesFields,
    });
  },

  getUser: (variables: UserPayload) => {
    return gql.query({
      operation: Operations.GET_USER,
      variables: {
        user_id: { value: variables.user_id, required: false, type: "String" },
      },
      fields: userFields,
    });
  },

  getEstimatePaymentFee: (variables: EstimatePaymentFeePayload) => {
    return gql.mutation({
      operation: Operations.ESTIMATE_PAYMENT_FEE,
      variables: {
        payment_route_id: { value: variables.payment_route_id, required: true, type: "String" },
        type: { value: variables.type, required: true, type: "PaymentType" },
        amount: { value: variables.amount, required: true, type: "Float" },
      },
      fields: [],
    });
  },

  getPaymentsLimits: (variables: PaymentsLimitsPayload) => {
    return gql.query({
      operation: Operations.GET_PAYMENTS_LIMITS,
      variables: {
        currency_id: { value: variables.currency_id, required: true, type: "String" },
        limit_group_id: { value: variables.limit_group_id, required: true, type: "String" },
      },
      fields: paymentsLimitsFields,
    });
  },

  // Live per-period used/limit/remaining from vakotrade. It's a mutation
  // server-side (read-style computation), not a query — mirror that.
  calculatePaymentsLimits: (variables: CalculatePaymentsLimitsPayload) => {
    return gql.mutation({
      operation: Operations.CALCULATE_PAYMENTS_LIMITS,
      variables: {
        currency_id: { value: variables.currency_id, required: true, type: "String" },
        type: { value: variables.type, required: true, type: "PaymentType" },
        notion_currency: { value: variables.notion_currency, required: true, type: "String" },
      },
      fields: calculatedPaymentsLimitsFields(variables.type),
    });
  },

  createWithdrawalFiat: (variables: CreateWithdrawalFiatPayload) => {
    const omitWalletId = isLegacyAndxExchange();
    return gql.mutation({
      operation: Operations.CREATE_WITHDRAWAL_FIAT,
      variables: {
        amount: { value: variables.amount, required: true, type: "Float" },
        payment_route_id: { value: variables.payment_route_id, required: true, type: "String" },
        currency_id: { value: variables.currency_id, required: true, type: "String" },
        mfa_token: { value: variables.mfa_token, required: false, type: "String" },
        ...(omitWalletId
          ? {}
          : { wallet_id: { value: variables.wallet_id, required: false, type: "String" } }),
      },
      fields: createWithdrawalFiatFields,
    });
  },

  getPaymentsRoutes: (variables: PaymentsRoutesPayload) => {
    return gql.query({
      operation: Operations.GET_PAYMENTS_ROUTES,
      variables: {
        payment_route_id: { value: variables.payment_route_id, required: true, type: "String" },
      },
      fields: paymentsRoutesFields,
    });
  },
};
